import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Welcome to CreativeHub — your 7-day Pro trial has started.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E5E7EB] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-white border-r border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#002FA7] flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">CreativeHub Website Pro Builder</span>
        </div>
        <div className="space-y-6">
          <h1 className="font-display text-5xl font-bold tracking-tighter leading-[1.05]">
            Start your<br />7-day Pro<br /><span className="text-[#002FA7]">trial today.</span>
          </h1>
          <ul className="space-y-2 text-base text-gray-700">
            <li>· Unlimited projects during trial</li>
            <li>· Canva-style drag-drop editor</li>
            <li>· Instant publishing to your subdomain</li>
            <li>· ZIP source code export (Pro)</li>
          </ul>
        </div>
        <p className="text-xs text-gray-400 font-mono">No card required to start. PayFast bills after the 7-day trial.</p>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 ch-fade" data-testid="register-form">
          <div className="space-y-2">
            <h2 className="font-display text-3xl font-bold tracking-tight">Create your account</h2>
            <p className="text-sm text-gray-500">7-day free Pro trial. No charge today.</p>
          </div>
          <div className="space-y-3">
            <Input
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="register-name-input"
              required
            />
            <Input
              type="email"
              placeholder="you@studio.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="register-email-input"
              required
            />
            <Input
              type="password"
              placeholder="Password (min 6 chars)"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="register-password-input"
              required
              minLength={6}
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="register-submit-button"
            className="w-full bg-[#002FA7] hover:bg-blue-800 text-white h-11"
          >
            {loading ? "Creating your studio…" : "Start free trial"}
          </Button>
          <p className="text-sm text-gray-500 text-center">
            Have an account?{" "}
            <Link to="/login" className="text-[#002FA7] font-medium hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
